import React, { useMemo, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useHistory } from 'react-router-dom'

import { TeamActions, getTeamMemberList, MemberState, getTeam, resendMemberInvite, TeamState } from 'state/teams'
import { selectTeamMembersList, createGetRequestsAreLoading, selectTeam } from 'state'
import { STATE_KEYS } from 'state/requests'
import { PredefinedModal, MODAL_TYPE } from 'components/Team/common/Modals'
import TeamRestricted from 'components/Shared/Permission/TeamRestricted'
import { useCheckTeamPermission } from 'hooks/permissions'
import { GridColDef, GridCellParams, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { StringCell } from 'components/Shared/MuiTable/Cell/StringCell'
import styled from 'particles/themes/styled'
import Header, { SwitchViewType } from 'components/Team/common/Header'
import styles from 'components/Team/Member/styles'
import { useNewTranslation } from 'hooks'
import MemberTable from 'components/Team/Member/MemberTable'
import RectangleButton from 'particles/Button/RectangleButton'
import Menu from 'particles/Menu'
import MenuItem from 'particles/MenuItem'
interface ModalState {
    type: MODAL_TYPE | undefined
    member: MemberState | undefined
}

interface TableProps {
    team: TeamState
    data: MemberState[]
    tooltipData: any[]
    hasCreateButton: boolean
}

const Table: React.FC<TableProps> = ({ team, data, tooltipData, hasCreateButton }) => {
    const [menuState, setMenuState] = useState<{
        anchorEl: null | HTMLButtonElement
        selectedMember?: MemberState
    }>({
        anchorEl: null,
        selectedMember: undefined,
    })

    const handleOpenMenu = (selectedMember: MemberState) => (event: React.MouseEvent<HTMLButtonElement>) => {
        setMenuState({ anchorEl: event.currentTarget, selectedMember })
    }
    const handleCloseMenu = () => {
        setMenuState({
            anchorEl: null,
            selectedMember: undefined,
        })
    }
    const [t] = useNewTranslation()
    const isAssignRolePermitted = useCheckTeamPermission(team, true)
    const isLoading = useSelector(createGetRequestsAreLoading([STATE_KEYS.TeamMembersList]))
    const memberListColumns = useMemo(() => {
        const columns: GridColDef<MemberState>[] = [
            {
                field: 'name',
                headerName: t<string>('manageTeams.teamPage.memberNameTableColumnLabel'),
                width: 372,
                cellClassName: 'title-cell',
                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                sortable: false,
            },
            {
                field: 'role',
                headerName: t<string>('manageTeams.teamPage.roleTableLabel'),
                width: 244,
                cellClassName: 'title-cell',
                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                sortable: false,
            },
            {
                field: 'email',
                headerName: t<string>('manageTeams.teamPage.memberEmailTableColumnLabel'),
                flex: 1,
                cellClassName: 'title-cell',
                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                sortable: false,
            },
        ]
        if (isAssignRolePermitted) {
            columns.push({
                field: 'actions',
                headerName: t<string>('manageTeams.allTeams.settingsTableColumnLabel'),
                width: 270,
                cellClassName: 'action-cell',
                renderCell: function CellText(params: GridCellParams): React.ReactElement {
                    return (
                        <RectangleButton
                            form="outlined"
                            size="s"
                            color="neutral"
                            icon="gear"
                            label={t('button.editSettings')}
                            onClick={handleOpenMenu(params.row.data)}
                        />
                    )
                },
                sortable: false,
            })
        }
        return columns
    }, [t, isAssignRolePermitted])
    const tableData: MemberState[] = useMemo(() => data, [data])

    const memberListRows: any[] = useMemo(() => {
        return tableData.map((data: any) => {
            return {
                id: data.uuid,
                name: { value: data.name },
                role: { value: String(data.role.name) },
                email: { value: String(data.email) },
                data: data,
                selectId: `member-tooltip-${data.uuid}`,
            }
        })
    }, [tableData])

    return (
        <>
            <MemberTable
                isLoading={isLoading}
                rows={memberListRows}
                columns={memberListColumns}
                hasCreateButton={hasCreateButton}
            />
            <Menu
                size="s"
                open={Boolean(menuState.anchorEl && menuState.selectedMember)}
                anchorEl={menuState.anchorEl}
                onClose={handleCloseMenu}
                width={180}
            >
                {tooltipData.map(
                    (option, index) =>
                        option.display(menuState.selectedMember) && (
                            <MenuItem.Text
                                key={`meneItem-${index}`}
                                text={option.text}
                                textColor={option.color}
                                onClick={() => {
                                    handleCloseMenu()
                                    option.onClick(menuState.selectedMember)
                                }}
                            />
                        ),
                )}
            </Menu>
        </>
    )
}

const Member: React.FC<any> = ({ className }) => {
    const [t] = useNewTranslation()
    const { uuid } = useParams<{ uuid: string }>()
    const [openModal, setOpenModal] = useState<ModalState>({ member: undefined, type: undefined })
    const dispatch = useDispatch()
    const members = useSelector(selectTeamMembersList(uuid as string))
    const team = useSelector(selectTeam(uuid as string))
    const isAssignRolePermitted = useCheckTeamPermission(team, true)
    const history = useHistory()

    const onOpenModal = (type: MODAL_TYPE | undefined, member?: MemberState): void => setOpenModal({ type, member })
    const onCloseModal = (): void => setOpenModal({ member: openModal.member, type: undefined })

    const onResendMemberInvite = (member: MemberState | undefined) =>
        member && dispatch(resendMemberInvite({ teamUuid: team.uuid, memberUuid: member.uuid }))

    const fetchData = (loadMore = false) => dispatch(getTeamMemberList({ loadMore, teamUuid: uuid }))

    const handleScroll = (): void => {
        if (Math.round(window.innerHeight + document.documentElement.scrollTop) < document.documentElement.offsetHeight)
            return
        fetchData(true)
    }

    useEffect(() => {
        fetchData()
        dispatch(getTeam({ teamUuid: uuid }))
        return (): void => {
            dispatch({ type: TeamActions.CLEAR_TEAM_MEMBER_LIST })
            dispatch({ type: TeamActions.CLEAR_TEAM_LIST })
        }
    }, [uuid])

    useEffect(() => {
        window.addEventListener('scroll', handleScroll)
        return (): void => window.removeEventListener('scroll', handleScroll)
    }, [])

    const tooltipData = [
        {
            text: t('button.editDetails'),
            display: () => isAssignRolePermitted,
            onClick: (member: MemberState) => onOpenModal(MODAL_TYPE.EDIT_MEMBER, member),
        },
        {
            text: t('button.resendEmailInvite'),
            display: () => isAssignRolePermitted,
            onClick: (member: MemberState) => onResendMemberInvite(member),
        },
        {
            text: t('button.removeFromTeam'),
            color: 'error',
            display: (member: MemberState) => isAssignRolePermitted && member?.is_removable_from_team,
            onClick: (member: MemberState) => onOpenModal(MODAL_TYPE.REMOVE_TEAM_MEMBER, member),
        },
        {
            text: t('button.deleteAccount'),
            color: 'error',
            display: () => isAssignRolePermitted,
            onClick: (member: MemberState) => onOpenModal(MODAL_TYPE.DELETE_MEMBER, member),
        },
    ]

    const handleSwitchViewType = (type: SwitchViewType) => {
        switch (type) {
            case SwitchViewType.MEMBERS:
                history.push(`/team/${uuid}/members`)
                break
            case SwitchViewType.ROLE_MANAGEMENT:
                history.push(`/teams/${uuid}/roles`)
                break
            case SwitchViewType.BRAND_ACCESS:
                history.push(`/team/${uuid}/brands`)
                break
            case SwitchViewType.BUSINESS_DETAILS:
                history.push(`/teams/${uuid}/business-details`)
                break
        }
    }

    return (
        <div className={className}>
            <Header
                isNotEdited={false}
                handleLeavePageWithoutSave={handleSwitchViewType}
                switchProps={{
                    currentViewType: 'members',
                }}
            />
            <TeamRestricted selectedTeam={team} isAssignRoles>
                <RectangleButton
                    form="filled"
                    size="m"
                    icon="plus"
                    label={t('button.addMember')}
                    onClick={(): void => onOpenModal(MODAL_TYPE.CREATE_MEMBER)}
                />
            </TeamRestricted>

            <div className="member-table">
                <Table team={team} data={members} tooltipData={tooltipData} hasCreateButton={isAssignRolePermitted} />
                <PredefinedModal onClose={onCloseModal} team={team} {...openModal} member={openModal.member} />
            </div>
        </div>
    )
}

export default styled(Member)`
    ${styles}
`
